import React, { useState, useEffect } from 'react';
import { keyframes } from '@emotion/react';
import { 
  Box, 
  VStack, 
  Text, 
  Icon, 
  Button, 
  Progress, 
  HStack,
  Circle
} from '@chakra-ui/react';
import { 
  FiClock, 
  FiAlertTriangle, 
  FiRefreshCw, 
  FiWifi
} from 'react-icons/fi';

// Create pulse animation
const pulseAnimation = keyframes`
  0% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.5); }
  70% { transform: scale(1); box-shadow: 0 0 0 10px rgba(16, 185, 129, 0); }
  100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(16, 185, 129, 0); }
`;

// Create countdown animation
const rotateAnimation = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const RateLimitError = ({ onRetry, retryAfter = 30 }) => {
  const [countdown, setCountdown] = useState(retryAfter);
  const [isRetrying, setIsRetrying] = useState(false);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleRetry = () => {
    if (onRetry && typeof onRetry === 'function') {
      setIsRetrying(true);
      
      // Simulate API call time
      setTimeout(() => {
        onRetry();
        setIsRetrying(false);
      }, 1000);
    }
  };

  // Calculate progress for the countdown
  const progress = ((retryAfter - countdown) / retryAfter) * 100;

  return (
    <Box
      bg="rgba(15, 27, 14, 0.8)"
      backdropFilter="blur(10px)"
      borderWidth="1px"
      borderColor="rgba(78, 224, 53, 0.2)"
      borderRadius="xl"
      overflow="hidden"
      p={6}
      position="relative"
      minH="300px"
      display="flex"
      alignItems="center"
      justifyContent="center"
      animation={countdown > 0 ? `${pulseAnimation} 2s infinite` : 'none'}
    >
      {/* Background pattern */}
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        opacity={0.05}
        backgroundImage="radial-gradient(circle at 30px 30px, rgba(16, 185, 129, 0.4) 2px, transparent 0)"
        backgroundSize="15px 15px"
        zIndex={0}
      />

      <VStack spacing={6} zIndex={1} textAlign="center" maxW="450px" mx="auto">
        {/* Icon with animated ring */}
        <Box position="relative" mb={2}>
          <Circle
            size="80px"
            bg="rgba(16, 185, 129, 0.1)"
            position="relative"
            zIndex={1}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Icon 
              as={countdown > 0 ? FiClock : FiWifi} 
              color="green.400" 
              boxSize={10}
              animation={isRetrying ? `${rotateAnimation} 1s linear infinite` : 'none'}
            />
          </Circle>
          
          {/* Animated ring */}
          {countdown > 0 && (
            <Box
              position="absolute"
              top="-6px"
              left="-6px"
              right="-6px"
              bottom="-6px"
              borderRadius="full"
              border="2px solid"
              borderColor="green.400"
              opacity="0.3"
              zIndex={0}
            />
          )}
        </Box>

        {/* Error message */}
        <Box>
          <Text fontSize="xl" fontWeight="bold" color="white" mb={2}>
            Rate Limit Reached
          </Text>
          <Text color="gray.300" fontSize="md">
            Our systems are cooldown to protect your experience. Please wait a moment before refreshing.
          </Text>
        </Box>

        {/* Countdown or retry button */}
        {countdown > 0 ? (
          <VStack spacing={3} w="full">
            <Progress
              value={progress}
              size="sm"
              colorScheme="green"
              borderRadius="full"
              w="full"
              bg="whiteAlpha.100"
            />
            <HStack spacing={1}>
              <Icon as={FiClock} color="green.400" />
              <Text color="white" fontWeight="medium">
                Try again in {countdown} seconds
              </Text>
            </HStack>
          </VStack>
        ) : (
          <Button
            leftIcon={<FiRefreshCw />}
            colorScheme="green"
            variant="outline"
            onClick={handleRetry}
            isLoading={isRetrying}
            loadingText="Retrying..."
            size="lg"
            _hover={{
              transform: "translateY(-2px)",
              boxShadow: "0 4px 12px rgba(16, 185, 129, 0.3)"
            }}
            transition="all 0.2s"
          >
            Refresh Now
          </Button>
        )}

        {/* Tips section */}
        <Box
          mt={4}
          p={4}
          bg="rgba(15, 27, 14, 0.8)"
          borderRadius="lg"
          w="full"
        >
          <Text fontSize="sm" color="gray.400" textAlign="center">
            <Icon as={FiAlertTriangle} color="yellow.400" mb="-2px" mr={1} />
            Tip: Avoid frequent refreshes to prevent rate limiting
          </Text>
        </Box>
      </VStack>
    </Box>
  );
};

export default RateLimitError;